import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Loader2, Copy, Check, Bot, RefreshCw, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClaudeMCPCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/mcp/status`, { withCredentials: true });
      setStatus(data);
    } catch { /* non-admin or error */ }
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const mcpUrl = status?.enabled
    ? `${process.env.REACT_APP_BACKEND_URL}/api/mcp/${status.token}`
    : '';

  const generate = async () => {
    if (status?.enabled && !window.confirm('Régénérer le jeton ? L\'ancienne URL cessera de fonctionner et il faudra la mettre à jour dans Claude.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/settings/mcp/generate`, {}, { withCredentials: true });
      toast.success('Jeton MCP généré');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setBusy(false);
  };

  const revoke = async () => {
    if (!window.confirm('Révoquer l\'accès de Claude ? La connexion cessera immédiatement.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/settings/mcp/revoke`, { withCredentials: true });
      toast.success('Accès MCP révoqué');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setBusy(false);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(mcpUrl);
    setCopied(true);
    toast.success('URL copiée');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return null;

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-5 space-y-4" data-testid="claude-mcp-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-neutral-900">Claude (serveur MCP)</h3>
            <p className="text-xs text-neutral-500">
              Interrogez vos données depuis Claude : clients, positions, leads, ROI, rapports, opportunités — lecture seule.
            </p>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${status?.enabled ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'}`} data-testid="mcp-status-badge">
          {status?.enabled ? 'Actif' : 'Non configuré'}
        </span>
      </div>

      {status?.enabled ? (
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-neutral-600 mb-1">URL du connecteur (secrète — ne la partagez pas)</p>
            <div className="flex gap-2">
              <code className="flex-1 text-xs bg-neutral-50 border border-neutral-200 rounded px-2 py-2 truncate" data-testid="mcp-url">
                {mcpUrl}
              </code>
              <Button size="sm" variant="outline" onClick={copyUrl} data-testid="mcp-copy-url">
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-900 space-y-1">
            <p className="font-semibold">Comment connecter Claude :</p>
            <p><b>claude.ai</b> (Pro/Max) : Paramètres → Connecteurs → « Ajouter un connecteur personnalisé » → collez l'URL ci-dessus</p>
            <p><b>Claude Desktop</b> : Paramètres → Connecteurs → « Ajouter un connecteur personnalisé » → collez l'URL</p>
            <p className="text-blue-700">Puis demandez par ex. : « Quel est le ROI de mes clients ce trimestre ? » ou « Liste les mots-clés qui ont progressé »</p>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[11px] text-neutral-400">
              {status.last_used_at
                ? `Dernière utilisation : ${new Date(status.last_used_at).toLocaleString('fr-FR')}`
                : 'Jamais utilisé pour le moment'}
              {' · '}{status.tools?.length || 0} outils exposés
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={generate} disabled={busy} data-testid="mcp-rotate-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Régénérer
              </Button>
              <Button size="sm" variant="outline" onClick={revoke} disabled={busy} className="text-red-600 hover:bg-red-50" data-testid="mcp-revoke-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Révoquer
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-neutral-500 flex-1 min-w-[220px]">
            Générez une URL secrète à coller dans Claude (claude.ai ou Desktop) pour lui donner un accès en lecture seule à vos données.
          </p>
          <Button size="sm" onClick={generate} disabled={busy} className="bg-gradient-to-r from-orange-500 to-amber-600 text-white" data-testid="mcp-generate-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Bot className="w-4 h-4 mr-1" />}
            Activer l'accès Claude
          </Button>
        </div>
      )}
    </div>
  );
};
